import { defineStore } from "pinia";
import { useLocalStorage } from "@vueuse/core";
import {
  buildEmptyResponseData,
  buildEmptyTier,
  buildEmptyValueForField,
  FIELD_TYPE_COLOR_PICKER_LIST,
  FIELD_TYPE_REPEATER_TEXT,
  getField,
  getMissingFieldKeys,
} from "%/questionnaire/schema-helpers";

const STORAGE_KEY_RESPONSE_DATA = "creator_questionnaire_response_data_v1";
const STORAGE_KEY_CURRENT_STEP = "creator_questionnaire_current_step_v1";
const STORAGE_KEY_SUBMITTED = "creator_questionnaire_is_submitted_v1";

export const useQuestionnaireStore = defineStore("questionnaire-store", {
  state: () => ({
    questionnaireSchema: null,
    responseData: useLocalStorage(STORAGE_KEY_RESPONSE_DATA, {}, { mergeDefaults: true }),
    currentStep: useLocalStorage(STORAGE_KEY_CURRENT_STEP, 1),
    isSubmitted: useLocalStorage(STORAGE_KEY_SUBMITTED, false),
    isSubmitting: false,
    submitError: "",
    hasAttemptedToAdvance: false,
  }),
  getters: {
    totalSteps() {
      return this.questionnaireSchema ? this.questionnaireSchema.steps.length : 0;
    },
    progressPercent() {
      if (this.totalSteps <= 0) {
        return 0;
      }
      return Math.round(this.currentStep / this.totalSteps * 100);
    },
    currentStepDefinition() {
      if (!this.questionnaireSchema) {
        return null;
      }
      return this.questionnaireSchema.steps.find(step => step.step === this.currentStep) || null;
    },
    missingFieldKeysForCurrentStep() {
      if (!this.questionnaireSchema) {
        return [];
      }
      return getMissingFieldKeys(this.questionnaireSchema, this.responseData, this.currentStep);
    },
  },
  actions: {
    // Called by the Vue renderer once the controller-injected schema has
    // landed in `questionnaireSchema`. Fills in any missing section / field
    // slots in `responseData` with type-appropriate defaults so the template
    // can read `responseData[section][field]` without optional-chaining.
    ensureResponseDataDefaults() {
      if (!this.questionnaireSchema) {
        return;
      }
      const defaults = buildEmptyResponseData(this.questionnaireSchema);
      for (const sectionKey of Object.keys(defaults)) {
        if (!this.responseData[sectionKey]) {
          this.responseData[sectionKey] = defaults[sectionKey];
          continue;
        }
        for (const fieldKey of Object.keys(defaults[sectionKey])) {
          if (this.responseData[sectionKey][fieldKey] === undefined) {
            this.responseData[sectionKey][fieldKey] = defaults[sectionKey][fieldKey];
          }
        }
      }
      this.coerceRepeaterFieldValues();
    },
    // Existing draft data may store a string for a field that became a
    // repeater_text after the schema changed; promote those strings to a
    // one-entry array so the renderer and mutators can treat them uniformly.
    coerceRepeaterFieldValues() {
      for (const step of this.questionnaireSchema.steps) {
        for (const section of step.sections) {
          for (const field of section.fields) {
            if (field.type !== FIELD_TYPE_REPEATER_TEXT && field.type !== FIELD_TYPE_COLOR_PICKER_LIST) {
              continue;
            }
            const stored = this.responseData[section.key][field.key];
            if (Array.isArray(stored)) {
              continue;
            }
            if (typeof stored === "string" && stored.trim() !== "") {
              this.responseData[section.key][field.key] = [stored];
              continue;
            }
            this.responseData[section.key][field.key] = [""];
          }
        }
      }
    },
    attemptGoToNextStep() {
      if (this.missingFieldKeysForCurrentStep.length > 0) {
        this.hasAttemptedToAdvance = true;
        return;
      }
      this.hasAttemptedToAdvance = false;
      this.goToNextStep();
    },
    goToNextStep() {
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
      }
    },
    goToPreviousStep() {
      if (this.currentStep > 1) {
        this.hasAttemptedToAdvance = false;
        this.currentStep--;
      }
    },
    toggleMultiSelectValue(sectionKey, fieldKey, value) {
      const list = this.responseData[sectionKey][fieldKey];
      const index = list.indexOf(value);
      if (index === -1) {
        list.push(value);
      } else {
        list.splice(index, 1);
      }
    },
    setSingleSelectValue(sectionKey, fieldKey, value) {
      this.responseData[sectionKey][fieldKey] = value;
    },
    addSubscriptionTier(sectionKey, fieldKey) {
      const field = getField(this.questionnaireSchema, sectionKey, fieldKey);
      this.responseData[sectionKey][fieldKey].push(buildEmptyTier(field));
    },
    removeSubscriptionTier(sectionKey, fieldKey, tierIndex) {
      this.responseData[sectionKey][fieldKey].splice(tierIndex, 1);
    },
    addRepeaterEntry(sectionKey, fieldKey) {
      const field = getField(this.questionnaireSchema, sectionKey, fieldKey);
      if (!field) {
        return;
      }
      const max = field.repeater_max || 0;
      const list = this.responseData[sectionKey][fieldKey];
      if (max > 0 && list.length >= max) {
        return;
      }
      list.push("");
    },
    removeRepeaterEntry(sectionKey, fieldKey, entryIndex) {
      const list = this.responseData[sectionKey][fieldKey];
      if (list.length <= 1) {
        list[entryIndex] = "";
        return;
      }
      list.splice(entryIndex, 1);
    },
    setRepeaterEntryValue(sectionKey, fieldKey, entryIndex, value) {
      this.responseData[sectionKey][fieldKey][entryIndex] = value;
    },
    setSocialLinkValue(sectionKey, fieldKey, platformKey, value) {
      const current = this.responseData[sectionKey][fieldKey];
      if (!current || typeof current !== "object") {
        const field = getField(this.questionnaireSchema, sectionKey, fieldKey);
        this.responseData[sectionKey][fieldKey] = buildEmptyValueForField(field);
      }
      this.responseData[sectionKey][fieldKey][platformKey] = value;
    },
    // Upload a picked branding asset to the blob store, then record the
    // returned URL (plus display metadata) on the response so submission only
    // ever carries URLs, never file bytes.
    uploadQuestionnaireAssetFile(sectionKey, fieldKey, file, onComplete, onError) {
      const formData = new FormData();
      formData.append("file", file);
      this.apiPost(
        "/survey/creator-questionnaire/asset",
        formData,
        responseData => {
          const uploadedUrl = responseData && responseData.url ? responseData.url : "";
          if (uploadedUrl === "") {
            onError("Upload failed. Please try again.");
            return;
          }
          this.responseData[sectionKey][fieldKey].push({
            name: file.name,
            size: file.size,
            type: file.type,
            url: uploadedUrl,
          });
          onComplete();
        },
        error => {
          const message = error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Upload failed. Please try again.";
          onError(message);
        },
      );
    },
    removeFileUploadEntry(sectionKey, fieldKey, entryIndex) {
      this.responseData[sectionKey][fieldKey].splice(entryIndex, 1);
    },
    attemptSubmitCreatorQuestionnaire() {
      if (this.missingFieldKeysForCurrentStep.length > 0) {
        this.hasAttemptedToAdvance = true;
        return;
      }
      this.hasAttemptedToAdvance = false;
      this.submitCreatorQuestionnaire();
    },
    submitCreatorQuestionnaire() {
      this.isSubmitting = true;
      this.submitError = "";

      const contact = this.responseData.contact || {};

      this.apiPost(
        "/survey/creator-questionnaire",
        {
          first_name: contact.first_name || "",
          last_name: contact.last_name || "",
          email: contact.email || "",
          questionnaire_version: this.questionnaireSchema.version,
          response_data: JSON.stringify({
            meta: {
              submitted_at: new Date().toISOString(),
            },
            ...this.responseData,
          }),
        },
        () => {
          this.isSubmitting = false;
          this.isSubmitted = true;
        },
        () => {
          this.isSubmitting = false;
          this.submitError = "Something went wrong submitting your questionnaire. Please try again.";
        },
      );
    },
  },
});
